import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import {
  TIMELINE_GRANULARITIES,
  accountLabel,
  comparePeriods,
  dailyTotals,
  filterTimeline,
  heatLevels,
  previousRange,
  spendingSeries,
  weekStart,
  type ExpenseAccount,
  type Platform,
  type TimelineGranularity,
} from "@torre/expenses";
import { useTheme } from "../../../context/ThemeContext";
import { apiErrorMessage, fetchExpenses, fetchPlatforms, type ExpenseRow } from "../services/expensesService";
import { fetchAccounts } from "../services/statementsService";
import { ErrorBox, Kpi, Notice, Page, Spinner } from "../components/ui";
import { CATEGORY_COLORS, categoryLabel, categoryOptions, money, monthLabel } from "../lib/format";
import { ui } from "../theme";

// ¿Cuándo se gastó? (#955): serie diaria / semanal / mensual, calendario de
// calor por día con sus movimientos, principales comercios y comparación con
// el periodo anterior. Los montos en otra moneda sin tipo de cambio van aparte.

const NS = "expensesStatements";
const RANGES = [30, 90, 180, 365] as const;
const DAY_MS = 86_400_000;
const HEAT = ["bg-gray-200 dark:bg-white/10", "bg-teal-200", "bg-teal-400", "bg-teal-600", "bg-teal-800"];

/** Hoy en Ciudad de México (YYYY-MM-DD). */
const todayMx = () => new Date(Date.now() - 6 * 3600 * 1000).toISOString().slice(0, 10);
const addDays = (iso: string, n: number) => new Date(Date.parse(`${iso}T00:00:00Z`) + n * DAY_MS).toISOString().slice(0, 10);

export default function TimelineSection() {
  const { t, i18n } = useTranslation(["common", NS]);
  const tt = (k: string, o?: Record<string, unknown>) => t(`${NS}:${k}`, o);
  const { isDarkMode } = useTheme();
  const c = ui(isDarkMode);

  const [granularity, setGranularity] = useState<TimelineGranularity>("dia");
  const [days, setDays] = useState<(typeof RANGES)[number]>(90);
  const [accountId, setAccountId] = useState("");
  const [category, setCategory] = useState("");
  const [platformId, setPlatformId] = useState("");
  const [includePayroll, setIncludePayroll] = useState(true);
  const [rows, setRows] = useState<ExpenseRow[]>([]);
  const [accounts, setAccounts] = useState<ExpenseAccount[]>([]);
  const [platforms, setPlatforms] = useState<Platform[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [day, setDay] = useState<string | null>(null);

  const to = todayMx();
  const from = addDays(to, -(days - 1));
  const prev = previousRange(from, to);

  const requestId = useRef(0);
  useEffect(() => {
    const id = ++requestId.current;
    setLoading(true);
    fetchExpenses({ from: prev.from, to, includePayroll })
      .then((r) => {
        if (id !== requestId.current) return;
        setRows(r.expenses);
        setError(null);
      })
      .catch((err) => id === requestId.current && setError(apiErrorMessage(err) || t("expenses.errors.load")))
      .finally(() => id === requestId.current && setLoading(false));
  }, [prev.from, to, includePayroll, t]);

  useEffect(() => {
    fetchAccounts()
      .then((a) => setAccounts(Array.isArray(a) ? a : []))
      .catch(() => setAccounts([]));
    fetchPlatforms()
      .then((p) => setPlatforms(Array.isArray(p) ? p : []))
      .catch(() => setPlatforms([]));
  }, []);

  const filtered = useMemo(() => filterTimeline(rows, { accountId, category, platformId }), [rows, accountId, category, platformId]);
  const current = useMemo(() => filtered.filter((e) => e.date >= from && e.date <= to), [filtered, from, to]);
  const series = useMemo(() => spendingSeries(current, from, to, granularity), [current, from, to, granularity]);
  const perDay = useMemo(() => dailyTotals(current, from, to), [current, from, to]);
  const levels = useMemo(() => heatLevels(perDay), [perDay]);
  const cmp = useMemo(() => comparePeriods(filtered, from, to, 10), [filtered, from, to]);

  const label = (key: string) =>
    granularity === "mes" ? monthLabel(key, i18n.language) : granularity === "semana" ? tt("timeline.weekOf", { date: key.slice(5) }) : key.slice(5);
  const chart = series.map((b) => ({ key: b.key, label: label(b.key), total: b.total }));

  // Calendario: columnas = semanas (lunes a domingo).
  const weeks: string[][] = [];
  for (let w = weekStart(from); w <= to; w = addDays(w, 7)) weeks.push(Array.from({ length: 7 }, (_, i) => addDays(w, i)));
  const dayRows = day ? current.filter((e) => e.date === day) : [];
  const foreignText = (f: Record<string, number>) =>
    Object.entries(f)
      .map(([cur, v]) => money(v, cur, i18n.language))
      .join(" + ");
  const hasForeign = Object.keys(cmp.current.foreign).length > 0;

  return (
    <Page title={tt("menu.timeline")} subtitle={tt("timeline.subtitle")}>
      <ErrorBox message={error} />

      <div className={`${c.card} grid grid-cols-2 gap-2 md:grid-cols-6`}>
        <select aria-label="granularity" className={c.input} value={granularity} onChange={(e) => setGranularity(e.target.value as TimelineGranularity)}>
          {TIMELINE_GRANULARITIES.map((g) => (
            <option key={g} value={g}>
              {tt(`timeline.granularity.${g}`)}
            </option>
          ))}
        </select>
        <select aria-label="range" className={c.input} value={days} onChange={(e) => setDays(Number(e.target.value) as (typeof RANGES)[number])}>
          {RANGES.map((r) => (
            <option key={r} value={r}>
              {tt(`timeline.ranges.${r}`)}
            </option>
          ))}
        </select>
        <select aria-label="account" className={c.input} value={accountId} onChange={(e) => setAccountId(e.target.value)}>
          <option value="">{tt("common.allAccounts")}</option>
          {accounts.map((a) => (
            <option key={a.id} value={a.id}>
              {accountLabel(a)}
            </option>
          ))}
        </select>
        <select aria-label="category" className={c.input} value={category} onChange={(e) => setCategory(e.target.value)}>
          <option value="">{tt("timeline.allCategories")}</option>
          {categoryOptions(i18n.language, { archived: true }).map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <select aria-label="platform" className={c.input} value={platformId} onChange={(e) => setPlatformId(e.target.value)}>
          <option value="">{tt("timeline.allPlatforms")}</option>
          {platforms.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
        <label className={`flex items-center gap-2 text-sm ${c.muted}`}>
          <input type="checkbox" className="h-4 w-4 accent-teal-600" checked={includePayroll} onChange={(e) => setIncludePayroll(e.target.checked)} />
          {tt("timeline.includePayroll")}
        </label>
      </div>

      {loading ? (
        <Spinner />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Kpi label={tt("timeline.total")} value={money(cmp.current.total, "MXN", i18n.language)} hint={tt("timeline.count", { count: cmp.current.count })} />
            <Kpi label={tt("timeline.previous")} value={money(cmp.previous.total, "MXN", i18n.language)} hint={`${cmp.previous.from} → ${cmp.previous.to}`} />
            <Kpi
              label={tt("timeline.change")}
              value={cmp.deltaPct == null ? money(cmp.delta, "MXN", i18n.language) : `${cmp.deltaPct > 0 ? "+" : ""}${cmp.deltaPct}%`}
              hint={cmp.deltaPct == null ? tt("timeline.noPrevious") : money(cmp.delta, "MXN", i18n.language)}
              tone={cmp.delta > 0 ? "bad" : cmp.delta < 0 ? "good" : undefined}
            />
            <Kpi label={tt("timeline.foreign")} value={hasForeign ? foreignText(cmp.current.foreign) : "—"} />
          </div>
          {hasForeign && <Notice tone="warn">{tt("timeline.foreignNote")}</Notice>}

          {current.length === 0 ? (
            <div className={`${c.card} text-center text-sm ${c.muted}`}>{tt("timeline.empty")}</div>
          ) : (
            <>
              <div className={c.card}>
                <h3 className={`mb-2 text-sm font-semibold ${c.text}`}>{tt("timeline.chart")}</h3>
                <div className="h-64 w-full" data-testid="timeline-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chart}>
                      <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? "#333" : "#e5e7eb"} vertical={false} />
                      <XAxis dataKey="label" tick={{ fontSize: 11, fill: isDarkMode ? "#9ca3af" : "#6b7280" }} interval="preserveStartEnd" />
                      <YAxis tick={{ fontSize: 11, fill: isDarkMode ? "#9ca3af" : "#6b7280" }} width={70} tickFormatter={(v: number) => money(v, "MXN", i18n.language).replace(/\.\d+$/, "")} />
                      <Tooltip formatter={(v) => money(Number(v), "MXN", i18n.language)} labelFormatter={(l) => String(l)} />
                      <Bar dataKey="total" fill="#0d9488" radius={[3, 3, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className={c.card}>
                <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
                  <h3 className={`text-sm font-semibold ${c.text}`}>{tt("timeline.calendar")}</h3>
                  <p className={`text-xs ${c.muted}`}>{tt("timeline.calendarHint")}</p>
                </div>
                <div className="overflow-x-auto">
                  <div className="flex gap-1" data-testid="heatmap">
                    {weeks.map((week) => (
                      <div key={week[0]} className="flex flex-col gap-1">
                        {week.map((d) => {
                          const inRange = d >= from && d <= to;
                          const b = perDay[d];
                          return (
                            <button
                              key={d}
                              type="button"
                              disabled={!inRange}
                              aria-label={`day-${d}`}
                              title={inRange ? `${d} · ${money(b?.total || 0, "MXN", i18n.language)}` : ""}
                              onClick={() => setDay(d)}
                              className={`h-3.5 w-3.5 rounded-sm ${inRange ? HEAT[levels[d] || 0] : "bg-transparent"} ${day === d ? "ring-2 ring-offset-1 ring-teal-500" : ""}`}
                            />
                          );
                        })}
                      </div>
                    ))}
                  </div>
                </div>
                <div className={`mt-2 flex items-center gap-1 text-[11px] ${c.muted}`}>
                  {tt("timeline.less")}
                  {HEAT.map((h) => (
                    <span key={h} className={`h-3 w-3 rounded-sm ${h}`} />
                  ))}
                  {tt("timeline.more")}
                </div>
                {day && (
                  <div className="mt-3" data-testid="day-detail">
                    <h4 className={`mb-1 text-sm font-semibold ${c.text}`}>{tt("timeline.dayTitle", { date: day })}</h4>
                    {dayRows.length === 0 ? (
                      <p className={`text-sm ${c.muted}`}>{tt("timeline.noDay")}</p>
                    ) : (
                      <table className="w-full text-sm">
                        <tbody>
                          {dayRows.map((e) => (
                            <tr key={e.id} className={`border-t ${c.border} ${c.text}`}>
                              <td className="py-1 pr-2">{e.vendor}</td>
                              <td className="py-1 pr-2 text-xs">
                                <span className="mr-1 inline-block h-2 w-2 rounded-full" style={{ background: CATEGORY_COLORS[e.category] }} />
                                {categoryLabel(e.category, i18n.language)}
                              </td>
                              <td className={`py-1 pr-2 text-xs ${c.muted}`}>{e.accountId ? accountLabel(accounts.find((a) => a.id === e.accountId)) : ""}</td>
                              <td className="py-1 text-right tabular-nums">
                                {e.amountBase == null ? (
                                  <span className="text-amber-600">{money(e.amount, e.currency, i18n.language)}</span>
                                ) : (
                                  money(e.amountBase, "MXN", i18n.language)
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </div>

              <div className={`${c.card} overflow-x-auto`}>
                <h3 className={`mb-2 text-sm font-semibold ${c.text}`}>{tt("timeline.topVendors")}</h3>
                <table className="w-full min-w-[520px] text-sm" data-testid="top-vendors">
                  <thead>
                    <tr className={`text-left text-xs ${c.muted}`}>
                      <th className="py-1 pr-2">{tt("timeline.vendor")}</th>
                      <th className="py-1 pr-2 text-right">{tt("timeline.current")}</th>
                      <th className="py-1 pr-2 text-right">{tt("timeline.previousCol")}</th>
                      <th className="py-1 text-right">{tt("timeline.change")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cmp.topVendors.map((v) => (
                      <tr key={v.key} className={`border-t ${c.border} ${c.text}`}>
                        <td className="py-1 pr-2">
                          {v.vendor}
                          <span className={`ml-1 text-xs ${c.muted}`}>×{v.count}</span>
                          {Object.keys(v.foreign).length > 0 && <span className="ml-1 text-xs text-amber-600">+ {foreignText(v.foreign)}</span>}
                        </td>
                        <td className="py-1 pr-2 text-right tabular-nums">{money(v.total, "MXN", i18n.language)}</td>
                        <td className={`py-1 pr-2 text-right tabular-nums ${c.muted}`}>{money(v.previous, "MXN", i18n.language)}</td>
                        <td className={`py-1 text-right tabular-nums ${v.delta > 0 ? "text-red-500" : v.delta < 0 ? "text-emerald-500" : c.muted}`}>
                          {v.delta > 0 ? "+" : ""}
                          {money(v.delta, "MXN", i18n.language)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}
    </Page>
  );
}
